import React from 'react';
import wearecoralWordmark from '../../assets/images/wordmarks/wearecoral-logo-gradient-white.svg';
import { getTicketsUrl } from '../../utils/ticketsUrl';

// Footer link groups. Headings render as tracked-uppercase eyebrows; the
// routes are the live www routes (the design kit's labels were mock).
const LINK_GROUPS: {
    heading: string;
    links: { label: string; href: string; external?: boolean }[];
}[] = [
    {
        heading: 'Platform',
        links: [
            { label: 'Home', href: '/' },
            { label: 'For Artists', href: '/for-artists' },
            { label: 'Tickets', href: getTicketsUrl(), external: true },
        ],
    },
    {
        heading: 'Company',
        links: [
            { label: 'About', href: '/about' },
            { label: 'Manifesto', href: '/manifesto' },
            { label: 'Principles', href: '/principles' },
            { label: 'Trust', href: '/trust' },
        ],
    },
    {
        heading: 'Support',
        links: [
            { label: 'Contact Us', href: '/contact-us' },
            { label: 'Terms', href: '/terms' },
            { label: 'Artist Terms', href: '/artist-terms' },
            { label: 'Privacy', href: '/privacy' },
        ],
    },
    {
        heading: 'Community',
        links: [
            {
                label: 'Instagram',
                href: 'https://www.instagram.com/wearecoral/',
                external: true,
            },
        ],
    },
];

// The coral-design "shared Coral footer": warm near-black ground (#241B14)
// with parchment text tiers (#E9DECB) and a peach link hover (#F6BE86) — its
// own calm surface, distinct from the editorial-ink page token.
const Footer: React.FC = () => {
    return (
        <footer className="bg-[#241B14] text-[#E9DECB]">
            <div className="max-w-screen-xl mx-auto px-6 md:px-12 pt-16 pb-10 md:pt-20 md:pb-11">
                <div className="grid grid-cols-2 gap-10 md:grid-cols-[1.6fr_1fr_1fr_1fr_1fr]">
                    {/* Brand + tagline */}
                    <div className="col-span-2 md:col-span-1">
                        <img
                            src={wearecoralWordmark}
                            alt="wearecoral"
                            className="h-[26px] w-auto"
                        />
                        <p className="font-cormorant text-[22px] leading-tight mt-5 max-w-[24ch] text-[#F3E8D5]">
                            Music thrives on intention, not extraction.
                        </p>
                    </div>

                    {LINK_GROUPS.map((group) => (
                        <div key={group.heading}>
                            <h5 className="text-[11px] tracking-[0.18em] uppercase mb-4 text-[#E9DECB]/50">
                                {group.heading}
                            </h5>
                            <ul className="list-none m-0 p-0 flex flex-col gap-2.5 text-sm">
                                {group.links.map((link) => (
                                    <li key={link.label}>
                                        <a
                                            href={link.href}
                                            {...(link.external
                                                ? { target: '_blank', rel: 'noopener noreferrer' }
                                                : {})}
                                            className="text-[#E9DECB]/80 hover:text-[#F6BE86] transition-colors duration-200"
                                        >
                                            {link.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="mt-12 pt-6 border-t border-[#E9DECB]/15 text-xs text-[#E9DECB]/50">
                    <a
                        href="/about#photography"
                        className="text-[#E9DECB]/50 hover:text-[#F6BE86] transition-colors duration-200"
                    >
                        Photography: Dominique Berns
                    </a>
                    <div className="mt-1.5 flex items-baseline justify-between gap-3">
                        {/* Shortened on phones so this row holds one line. */}
                        <span>
                            &copy; {new Date().getFullYear()} wearecoral
                            <span className="hidden sm:inline"> Pty Ltd</span>.{' '}
                            <span className="hidden min-[390px]:inline">A social enterprise.</span>
                        </span>
                        <span className="whitespace-nowrap">We cooperate.</span>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
